import { ENTITY } from '@/enums';

export interface IAccessPortal {
  userIdEmail: string; // format: ${userId - email},
  userAgent: string;
  ipAddress: string;
  location: string;
  loginAt: Date;
  logoutAt: Date;
}
export interface IAPIResult {
  data: {
    data: IAccessPortal[];
    nextToken: string | null;
  };
}

export enum AuditLogType {
  USER_LOGIN = 'USER_LOGIN',
  USER_LOGOUT = 'USER_LOGOUT',
  STAFF_CREATE = 'STAFF_CREATE',
  STAFF_UPDATE = 'STAFF_UPDATE',
  STAFF_DELETE = 'STAFF_DELETE',
  STAFF_EXPORT = 'STAFF_EXPORT',
  PARTNER_CREATE = 'PARTNER_CREATE',
  PARTNER_UPDATE = 'PARTNER_UPDATE',
  PARTNER_DELETE = 'PARTNER_DELETE',
  PARTNER_EXPORT = 'PARTNER_EXPORT',
  ORG_CREATE = 'ORG_CREATE',
  ORG_UPDATE = 'ORG_UPDATE',
  ORG_DELETE = 'ORG_DELETE',
  USER_CREATE = 'USER_CREATE',
  USER_UPDATE = 'USER_UPDATE',
  USER_DELETE = 'USER_DELETE',
  USER_EXPORT = 'USER_EXPORT',
  USER_IMPORT = 'USER_IMPORT',
  USER_SENT_INVITE = 'USER_SENT_INVITE',
  PRODUCT_CREATE = 'PRODUCT_CREATE',
  PRODUCT_UPDATE = 'PRODUCT_UPDATE',
  PRODUCT_DELETE = 'PRODUCT_DELETE',
  PRODUCT_EXPORT = 'PRODUCT_EXPORT',
  PRODUCT_DEACTIVATE = 'PRODUCT_DEACTIVATE',
  PRODUCT_RESTORE = 'PRODUCT_RESTORE',
  ORDER_CREATE = 'ORDER_CREATE',
  ORDER_UPDATE = 'ORDER_UPDATE',
  ORDER_DELETE = 'ORDER_DELETE',
  ORDER_EXPORT = 'ORDER_EXPORT',
  ORDER_RESTORE = 'ORDER_RESTORE',
  ORDER_CHANGE_STATUS = 'ORDER_CHANGE_STATUS',
  ORDER_CHANGE_PAYMENT_STATUS = 'ORDER_CHANGE_PAYMENT_STATUS',
  ORDER_RESOLVE_NOTE = 'ORDER_RESOLVE_NOTE',
  ORDER_CONFIRMATION = 'ORDER_CONFIRMATION',
  PROJECT_CREATE = 'PROJECT_CREATE',
  PROJECT_UPDATE = 'PROJECT_UPDATE',
  PROJECT_DELETE = 'PROJECT_DELETE',
  KIT_SCAN = 'KIT_SCAN',
  KIT_CHANGE_STATUS = 'KIT_CHANGE_STATUS',
  KIT_ADD_LAB_ID = 'KIT_ADD_LAB_ID',
  KIT_UPDATE_NOTE = 'KIT_UPDATE_NOTE',
  KIT_DELETE = 'KIT_DELETE',
  KIT_RESTORE = 'KIT_RESTORE',
  KIT_EXTRACT_SAMPLE = 'KIT_EXTRACT_SAMPLE',
  KIT_SAMPLE_EDIT = 'KIT_SAMPLE_EDIT',
  KIT_DELETE_SAMPLE = 'KIT_DELETE_SAMPLE',
  KIT_RESTORE_SAMPLE = 'KIT_RESTORE_SAMPLE',
  KIT_EXPORT = 'KIT_EXPORT',
  BATCH_CREATE = 'BATCH_CREATE',
  BATCH_SCAN_DNA_TO_CREATE_SAMPLE = 'BATCH_SCAN_DNA_TO_CREATE_SAMPLE',
  BATCH_DELETE = 'BATCH_DELETE',
  BATCH_SEQUENCE_DELETE = 'BATCH_SEQUENCE_DELETE',
  BATCH_SEQUENCE_CHANGE_STATUS = 'BATCH_SEQUENCE_CHANGE_STATUS',
  COACH_USER_CREATE = 'COACH_USER_CREATE',
  COACH_USER_APPROVE = 'COACH_USER_APPROVE',
  COACH_PROGRAM_CREATE = 'COACH_PROGRAM_CREATE',
  COACH_PROGRAM_UPDATE = 'COACH_PROGRAM_UPDATE',
  COACH_PROGRAM_DELETE = 'COACH_PROGRAM_DELETE',
  SEQUENCING_PARTNER_CREATE = 'SEQUENCING_PARTNER_CREATE',
  SEQUENCING_PARTNER_UPDATE = 'SEQUENCING_PARTNER_UPDATE',
  SEQUENCING_PARTNER_DELETE = 'SEQUENCING_PARTNER_DELETE',
  SEQUENCING_PARTNER_RESTORE = 'SEQUENCING_PARTNER_RESTORE',
  SEQUENCING_TYPE_CREATE = 'SEQUENCING_TYPE_CREATE',
  SEQUENCING_TYPE_UPDATE = 'SEQUENCING_TYPE_UPDATE',
  SEQUENCING_TYPE_DELETE = 'SEQUENCING_TYPE_DELETE',
  SEQUENCING_TYPE_RESTORE = 'SEQUENCING_TYPE_RESTORE',
  SEQUENCING_SHORTNAME_UPDATE_MAPPING = 'SEQUENCING_SHORTNAME_UPDATE_MAPPING',
  REPORT_PUBLISH = 'REPORT_PUBLISH',
  REPORT_UNPUBLISH = 'REPORT_UNPUBLISH',
}

export enum DEFAULT_OBJECT {
  DASHBOARD = 'Dashboard',
  USER = 'User',
  STAFF = 'Staff',
  ORG = 'Org',
  PII_DATA = 'PII Data',
  ORDER = 'Order',
  ORDER_STATUS = 'Order Status',
  PARTNER = 'Partner',
  KIT = 'Kit',
  KIT_STATUS = 'Kit Status',
  PRODUCT = 'Product',
  BATCH = 'Batch',
  SEQUENCE_STATUS = 'Sequence Status',
  PROJECT = 'Project',
  COACH_USER = 'Coach User',
  COACH_PROGRAM = 'Coach Program',
  REPORT = 'Report',
  SEQUENCING_TYPE = 'Sequencing Type',
  SEQUENCING_PARTNER = 'Sequencing Partner',
  SEQUENCING_SHORTNAME = 'Sequencing Shortname',
  SAMPLE = 'Sample',
}

export enum LogAction {
  LOGIN = 'Login',
  LOGOUT = 'Logout',
  CREATE = 'Create',
  DELETE = 'Delete',
  UPDATE = 'Modify',
  SEND_INVITATION = 'Send invitation',
  IMPORT = 'Import',
  EXPORT = 'Export',
  READ = 'Read records',
  RESTORE = 'Restore',
  EXTRACT = 'Extract',
  SCAN_DNA = 'Scan DNA',
  SCAN_KIT = 'Scan Kit',
  DEACTIVATE = 'Deactivate',
  APPROVE = 'Approve',
  HOLE_UP = 'Hole Up',
  PUBLISH = 'Publish',
  CONFIRM = 'Confirm',
}
export const ACCESS_PII_ACTION: {
  [key: string]: LogAction;
} = {
  GET: LogAction.READ,
  PUT: LogAction.UPDATE,
  PATCH: LogAction.UPDATE,
  POST: LogAction.CREATE,
  DELETE: LogAction.DELETE,
  APPROVE: LogAction.APPROVE,
  HOLE_UP: LogAction.HOLE_UP,
  PUBLISH: LogAction.PUBLISH,
  CONFIRM: LogAction.CONFIRM,
};
export enum LogStatus {
  SUCCESS = 'Success',
  FAILED = 'Failure',
}

export const LogStatusColor: {
  [key: string]: { color: string; textColor: string };
} = {
  [LogStatus.SUCCESS]: { color: '#EBF9EB', textColor: '#00AB00' },
  [LogStatus.FAILED]: { color: '#FF634E', textColor: '#FFF3F1' },
};
export const ACTION_IN_LOG: {
  [key: string]: LogAction;
} = {
  [AuditLogType.USER_LOGIN]: LogAction.LOGIN,
  [AuditLogType.USER_LOGOUT]: LogAction.LOGOUT,
  [AuditLogType.USER_CREATE]: LogAction.CREATE,
  [AuditLogType.USER_UPDATE]: LogAction.UPDATE,
  [AuditLogType.USER_DELETE]: LogAction.DELETE,
  [AuditLogType.USER_IMPORT]: LogAction.IMPORT,
  [AuditLogType.USER_EXPORT]: LogAction.EXPORT,
  [AuditLogType.USER_SENT_INVITE]: LogAction.SEND_INVITATION,
  [AuditLogType.STAFF_CREATE]: LogAction.CREATE,
  [AuditLogType.STAFF_UPDATE]: LogAction.UPDATE,
  [AuditLogType.STAFF_DELETE]: LogAction.DELETE,
  [AuditLogType.STAFF_EXPORT]: LogAction.EXPORT,
  [AuditLogType.ORG_CREATE]: LogAction.CREATE,
  [AuditLogType.ORG_UPDATE]: LogAction.UPDATE,
  [AuditLogType.ORG_DELETE]: LogAction.DELETE,
  [AuditLogType.PRODUCT_CREATE]: LogAction.CREATE,
  [AuditLogType.PRODUCT_UPDATE]: LogAction.UPDATE,
  [AuditLogType.PRODUCT_DELETE]: LogAction.DELETE,
  [AuditLogType.PRODUCT_EXPORT]: LogAction.EXPORT,
  [AuditLogType.PRODUCT_RESTORE]: LogAction.RESTORE,
  [AuditLogType.PRODUCT_DEACTIVATE]: LogAction.DEACTIVATE,
  [AuditLogType.ORDER_CREATE]: LogAction.CREATE,
  [AuditLogType.ORDER_UPDATE]: LogAction.UPDATE,
  [AuditLogType.ORDER_CONFIRMATION]: LogAction.CONFIRM,
  [AuditLogType.ORDER_CHANGE_STATUS]: LogAction.UPDATE,
  [AuditLogType.ORDER_RESOLVE_NOTE]: LogAction.UPDATE,
  [AuditLogType.ORDER_DELETE]: LogAction.DELETE,
  [AuditLogType.ORDER_EXPORT]: LogAction.EXPORT,
  [AuditLogType.ORDER_RESTORE]: LogAction.RESTORE,
  [AuditLogType.PARTNER_CREATE]: LogAction.CREATE,
  [AuditLogType.PARTNER_UPDATE]: LogAction.UPDATE,
  [AuditLogType.PARTNER_DELETE]: LogAction.DELETE,
  [AuditLogType.PARTNER_EXPORT]: LogAction.EXPORT,
  [AuditLogType.KIT_CHANGE_STATUS]: LogAction.UPDATE,
  [AuditLogType.KIT_ADD_LAB_ID]: LogAction.UPDATE,
  [AuditLogType.KIT_DELETE]: LogAction.DELETE,
  [AuditLogType.KIT_DELETE_SAMPLE]: LogAction.DELETE,
  [AuditLogType.KIT_EXTRACT_SAMPLE]: LogAction.EXTRACT,
  [AuditLogType.KIT_RESTORE_SAMPLE]: LogAction.RESTORE,
  [AuditLogType.KIT_EXPORT]: LogAction.EXPORT,
  [AuditLogType.KIT_RESTORE]: LogAction.RESTORE,
  [AuditLogType.KIT_SCAN]: LogAction.SCAN_KIT,
  [AuditLogType.KIT_SAMPLE_EDIT]: LogAction.DELETE,
  [AuditLogType.KIT_UPDATE_NOTE]: LogAction.UPDATE,
  [AuditLogType.BATCH_CREATE]: LogAction.CREATE,
  [AuditLogType.BATCH_DELETE]: LogAction.DELETE,
  [AuditLogType.BATCH_SCAN_DNA_TO_CREATE_SAMPLE]: LogAction.SCAN_DNA,
  [AuditLogType.BATCH_SEQUENCE_DELETE]: LogAction.DELETE,
  [AuditLogType.BATCH_SEQUENCE_CHANGE_STATUS]: LogAction.UPDATE,
  [AuditLogType.PROJECT_CREATE]: LogAction.CREATE,
  [AuditLogType.PROJECT_UPDATE]: LogAction.UPDATE,
  [AuditLogType.PROJECT_DELETE]: LogAction.DELETE,
  [AuditLogType.COACH_PROGRAM_CREATE]: LogAction.CREATE,
  [AuditLogType.COACH_PROGRAM_UPDATE]: LogAction.UPDATE,
  [AuditLogType.COACH_PROGRAM_DELETE]: LogAction.DELETE,
  [AuditLogType.COACH_USER_CREATE]: LogAction.CREATE,
  [AuditLogType.COACH_USER_APPROVE]: LogAction.APPROVE,
  [AuditLogType.SEQUENCING_PARTNER_CREATE]: LogAction.CREATE,
  [AuditLogType.SEQUENCING_PARTNER_UPDATE]: LogAction.UPDATE,
  [AuditLogType.SEQUENCING_PARTNER_DELETE]: LogAction.DELETE,
  [AuditLogType.SEQUENCING_PARTNER_RESTORE]: LogAction.RESTORE,
  [AuditLogType.SEQUENCING_TYPE_CREATE]: LogAction.CREATE,
  [AuditLogType.SEQUENCING_TYPE_UPDATE]: LogAction.UPDATE,
  [AuditLogType.SEQUENCING_TYPE_DELETE]: LogAction.DELETE,
  [AuditLogType.SEQUENCING_TYPE_RESTORE]: LogAction.RESTORE,
  [AuditLogType.SEQUENCING_SHORTNAME_UPDATE_MAPPING]: LogAction.UPDATE,
  [AuditLogType.REPORT_PUBLISH]: LogAction.PUBLISH,
  [AuditLogType.REPORT_UNPUBLISH]: LogAction.HOLE_UP,
};

export const OBJECT_IN_LOG: {
  [key: string]: DEFAULT_OBJECT;
} = {
  [AuditLogType.USER_LOGIN]: DEFAULT_OBJECT.DASHBOARD,
  [AuditLogType.USER_LOGOUT]: DEFAULT_OBJECT.DASHBOARD,
  [AuditLogType.USER_CREATE]: DEFAULT_OBJECT.USER,
  [AuditLogType.USER_UPDATE]: DEFAULT_OBJECT.USER,
  [AuditLogType.USER_DELETE]: DEFAULT_OBJECT.USER,
  [AuditLogType.USER_IMPORT]: DEFAULT_OBJECT.USER,
  [AuditLogType.USER_EXPORT]: DEFAULT_OBJECT.USER,
  [AuditLogType.USER_SENT_INVITE]: DEFAULT_OBJECT.USER,
  [AuditLogType.STAFF_CREATE]: DEFAULT_OBJECT.STAFF,
  [AuditLogType.STAFF_UPDATE]: DEFAULT_OBJECT.STAFF,
  [AuditLogType.STAFF_DELETE]: DEFAULT_OBJECT.STAFF,
  [AuditLogType.STAFF_EXPORT]: DEFAULT_OBJECT.STAFF,
  [AuditLogType.ORG_CREATE]: DEFAULT_OBJECT.ORG,
  [AuditLogType.ORG_UPDATE]: DEFAULT_OBJECT.ORG,
  [AuditLogType.ORG_DELETE]: DEFAULT_OBJECT.ORG,
  [AuditLogType.PRODUCT_CREATE]: DEFAULT_OBJECT.PRODUCT,
  [AuditLogType.PRODUCT_UPDATE]: DEFAULT_OBJECT.PRODUCT,
  [AuditLogType.PRODUCT_DELETE]: DEFAULT_OBJECT.PRODUCT,
  [AuditLogType.PRODUCT_EXPORT]: DEFAULT_OBJECT.PRODUCT,
  [AuditLogType.PRODUCT_RESTORE]: DEFAULT_OBJECT.PRODUCT,
  [AuditLogType.PRODUCT_DEACTIVATE]: DEFAULT_OBJECT.PRODUCT,
  [ENTITY.TENANT_ADDRESS]: DEFAULT_OBJECT.PII_DATA,
  [ENTITY.USER_ADDRESS]: DEFAULT_OBJECT.PII_DATA,
  user: DEFAULT_OBJECT.PII_DATA,
  [AuditLogType.ORDER_CREATE]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.ORDER_UPDATE]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.ORDER_CONFIRMATION]: DEFAULT_OBJECT.ORDER_STATUS,
  [AuditLogType.ORDER_CHANGE_STATUS]: DEFAULT_OBJECT.ORDER_STATUS,
  [AuditLogType.ORDER_RESOLVE_NOTE]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.ORDER_DELETE]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.ORDER_EXPORT]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.ORDER_RESTORE]: DEFAULT_OBJECT.ORDER,
  [AuditLogType.PARTNER_CREATE]: DEFAULT_OBJECT.PARTNER,
  [AuditLogType.PARTNER_UPDATE]: DEFAULT_OBJECT.PARTNER,
  [AuditLogType.PARTNER_DELETE]: DEFAULT_OBJECT.PARTNER,
  [AuditLogType.PARTNER_EXPORT]: DEFAULT_OBJECT.PARTNER,
  [AuditLogType.KIT_CHANGE_STATUS]: DEFAULT_OBJECT.KIT_STATUS,
  [AuditLogType.KIT_ADD_LAB_ID]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_DELETE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_DELETE_SAMPLE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_EXTRACT_SAMPLE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_RESTORE_SAMPLE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_EXPORT]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_RESTORE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_SCAN]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_UPDATE_NOTE]: DEFAULT_OBJECT.KIT,
  [AuditLogType.KIT_SAMPLE_EDIT]: DEFAULT_OBJECT.SAMPLE,
  [AuditLogType.BATCH_CREATE]: DEFAULT_OBJECT.BATCH,
  [AuditLogType.BATCH_DELETE]: DEFAULT_OBJECT.BATCH,
  [AuditLogType.BATCH_SCAN_DNA_TO_CREATE_SAMPLE]: DEFAULT_OBJECT.BATCH,
  [AuditLogType.BATCH_SEQUENCE_DELETE]: DEFAULT_OBJECT.BATCH,
  [AuditLogType.BATCH_SEQUENCE_CHANGE_STATUS]: DEFAULT_OBJECT.SEQUENCE_STATUS,
  [AuditLogType.PROJECT_CREATE]: DEFAULT_OBJECT.PROJECT,
  [AuditLogType.PROJECT_UPDATE]: DEFAULT_OBJECT.PROJECT,
  [AuditLogType.PROJECT_DELETE]: DEFAULT_OBJECT.PROJECT,
  [AuditLogType.COACH_PROGRAM_CREATE]: DEFAULT_OBJECT.COACH_PROGRAM,
  [AuditLogType.COACH_PROGRAM_UPDATE]: DEFAULT_OBJECT.COACH_PROGRAM,
  [AuditLogType.COACH_PROGRAM_DELETE]: DEFAULT_OBJECT.COACH_PROGRAM,
  [AuditLogType.COACH_USER_CREATE]: DEFAULT_OBJECT.COACH_USER,
  [AuditLogType.COACH_USER_APPROVE]: DEFAULT_OBJECT.COACH_USER,
  [AuditLogType.SEQUENCING_PARTNER_CREATE]: DEFAULT_OBJECT.SEQUENCING_PARTNER,
  [AuditLogType.SEQUENCING_PARTNER_UPDATE]: DEFAULT_OBJECT.SEQUENCING_PARTNER,
  [AuditLogType.SEQUENCING_PARTNER_DELETE]: DEFAULT_OBJECT.SEQUENCING_PARTNER,
  [AuditLogType.SEQUENCING_PARTNER_RESTORE]: DEFAULT_OBJECT.SEQUENCING_PARTNER,
  [AuditLogType.SEQUENCING_TYPE_CREATE]: DEFAULT_OBJECT.SEQUENCING_TYPE,
  [AuditLogType.SEQUENCING_TYPE_UPDATE]: DEFAULT_OBJECT.SEQUENCING_TYPE,
  [AuditLogType.SEQUENCING_TYPE_DELETE]: DEFAULT_OBJECT.SEQUENCING_TYPE,
  [AuditLogType.SEQUENCING_TYPE_RESTORE]: DEFAULT_OBJECT.SEQUENCING_TYPE,
  [AuditLogType.SEQUENCING_SHORTNAME_UPDATE_MAPPING]:
    DEFAULT_OBJECT.SEQUENCING_SHORTNAME,
  [AuditLogType.REPORT_PUBLISH]: DEFAULT_OBJECT.REPORT,
  [AuditLogType.REPORT_UNPUBLISH]: DEFAULT_OBJECT.REPORT,
};

export interface ILog {
  user: string;
  action: LogAction;
  object: string;
  createdAt: Date | string;
  updatedAt?: Date | string | null;
  deletedAt?: Date | string | null;
  browserVersion?: string;
  device: string;
  ipAddress: string;
  location: string;
  status: LogStatus;
  errorMessage?: string;
}
